import React, { useState } from "react";
import { images } from "assets/images";
import { useTranslation } from "react-i18next";
import { useLocationForReportType } from "hooks/useLocationForReportType";
import { Image, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { XLg } from "react-bootstrap-icons";
import {
  AwaitingASecondInspectionStatuseIds,
  DecisionTypesCode,
  ERROR_MESSAGE,
  ReportRejectionReasonType,
  StatusesEnums,
  UserRole,
} from "components/Reports/reportAssets/assets";
import { useRole } from "hooks/useRole";
import {
  usePullReportToControlCenterMutation,
  usePullReportToSeniorControllerMutation,
} from "features/api/reports/reportsSlice";
import { toast } from "react-toastify";
import { ReportInformationFirstStep } from "models/ReportInformationFirstStep";
import ModalBootstrap from "components/bootstrap/ModalBootstrap";
import Log from "components/Log/Log";

type ReportTitleProps = {
  reportDetails: ReportInformationFirstStep;
  isMessagesExist?: boolean;
  handleClose?: () => void;
};

const ReportTitle = ({ reportDetails, handleClose }: ReportTitleProps) => {
  const { t } = useTranslation();
  const {
    reportId,
    reportStatusID,
    reportIsOrigin,
    duplicatedReportsIds,
    reportIsDuplicate,
    originReportId,
    isMessagesExist,
    decisionTypeID,
    rejectionReasonID,
  } = reportDetails;

  const checkStatusForSeniorController = () => {
    if (AwaitingASecondInspectionStatuseIds.includes(reportStatusID)) {
      return true;
    }
    return false;
  };

  const checkValidationForWithdrawnByControlCenter = () => {
    if (
      reportStatusID !== StatusesEnums.START_PROCESS_SEND_TO_POLICE.id &&
      reportStatusID !== StatusesEnums.WITHDRWAN_BY_CONTROL_CENTER.id &&
      reportStatusID !== StatusesEnums.AWAITING_FEEDBACK.id &&
      (reportStatusID !== StatusesEnums.COMPLETED.id ||
        (decisionTypeID === DecisionTypesCode.DisqualificationReport &&
          rejectionReasonID !==
            ReportRejectionReasonType.reportIncompleteInTime))
    )
      return true;

    return false;
  };

  const role = useRole();
  const isSenior = role === UserRole.SENIOR;
  const isControlCenter = role === UserRole.CONTROLCENTER;
  const locationString = useLocationForReportType();

  const isFollowUp = locationString === "followup";
  const isMessagesScreen = locationString === "followup-messages";
  const [pullReportForSenior] = usePullReportToSeniorControllerMutation();
  const [pullReportForControlCenter] = usePullReportToControlCenterMutation();
  const navigate = useNavigate();
  const [isPullLoading, setIsPullLoading] = useState(false);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [pulledByControlCenter, setPulledByControlCenter] = useState(false);

  const handlePullButtonClick = async () => {
    setIsPullLoading(true);
    try {
      const res = isControlCenter
        ? await pullReportForControlCenter(reportId).unwrap()
        : await pullReportForSenior(reportId).unwrap();
      if (res) {
        navigate(`/reports/inprogress/${reportId}`);
        setIsPullLoading(false);
        if (isControlCenter) setPulledByControlCenter(true);
      }
    } catch (error) {
      setIsPullLoading(false);
      setPulledByControlCenter(false);
      console.error("Error:", error);
      toast.error(ERROR_MESSAGE);
    }
  };

  const renderPullReportButton = () => {
    if (
      (checkStatusForSeniorController() && isSenior) ||
      (isControlCenter &&
        checkValidationForWithdrawnByControlCenter() &&
        !pulledByControlCenter)
    ) {
      return (
        <button
          type="button"
          className="tagContainer"
          onClick={handlePullButtonClick}
        >
          {isPullLoading ? (
            <Spinner animation="border" variant="secondary" />
          ) : (
            t("pullReport")
          )}
        </button>
      );
    }
    return null;
  };

  let reportTitle = t("titlePendingReports");

  if (isFollowUp) {
    reportTitle = isControlCenter ? t("allReports") : t("followUpReports");
  }

  const getDuplicatesTitle = () => {
    if (reportIsOrigin && duplicatedReportsIds.length > 0) {
      return (
        <span className="duplicates-title">
          ({duplicatedReportsIds?.length} {t("duplicatedReports")})
        </span>
      );
    }
    if (reportIsDuplicate) {
      return (
        <span className="duplicates-title">
          ( {t("originReport")} - {originReportId} )
        </span>
      );
    }
    return null;
  };

  const reportNumberLabel = (
    <div className="felx-container">
      <div style={handleClose && { display: "flex", alignItems: "center" }}>
        {!isMessagesScreen && (
          <span className="default-color">
            {reportTitle}
            <Image
              src={images.leftArrow}
              alt={images.leftArrow}
              className="defualt-padding-horizontal icon-directional"
            />
          </span>
        )}
        {handleClose && (
          <XLg onClick={handleClose} className="closeReportButton" />
        )}
        <span className="default-color">
          {t("labelReportNumberFlipped")} {reportId}
        </span>
        {getDuplicatesTitle()}
      </div>
      <div className="buttons-container-flex-row">
        {isMessagesExist && isControlCenter && !isMessagesScreen && (
          <button
            type="button"
            className="tagContainer"
            onClick={() =>
              navigate(`/reports/followup-messages`, { state: { reportId } })
            }
          >
            {t("watchMessages")}{" "}
          </button>
        )}
        {renderPullReportButton()}
        <Image
          src={images.clock}
          alt={images.clock}
          className="defualt-padding-horizontal log-icon"
          onClick={() => setIsLogModalOpen(true)}
        />
      </div>
      <ModalBootstrap
        onHide={() => setIsLogModalOpen(false)}
        show={isLogModalOpen}
        dialogClassName="log-modal"
      >
        <Log />
      </ModalBootstrap>
    </div>
  );
  return reportNumberLabel;
};

export default ReportTitle;
